import { Link } from "react-router-dom";
import { ArrowUpRight, Users, Music, BookOpen, Send, Calendar, MapPin, Clock } from "lucide-react";
import ShareButton from "@/components/ShareButton";

const pillars = [
  {
    icon: Users,
    title: "Community",
    text: "Build real friendships that go past small talk.",
  },
  {
    icon: Music,
    title: "Worship",
    text: "Gather to worship Jesus — voices loud, distractions off.",
  },
  {
    icon: BookOpen,
    title: "Scripture",
    text: "Hear God's Word. Wrestle with real questions.",
  },
  {
    icon: Send,
    title: "Mission",
    text: "Live out your faith in your school and your city.",
  },
];

const HomePage = () => {
  return (
    <>
      {/* HERO */}
      <section
        className="relative pt-36 md:pt-40 pb-16 md:pb-24 overflow-hidden"
        data-testid="home-hero"
      >
        {/* Richmond skyline bg */}
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1628116904332-7c0ec0cbc623?fm=jpg&q=85&w=2400&auto=format&fit=crop&ixlib=rb-4.1.0')",
            backgroundSize: "cover",
            backgroundPosition: "center",
            opacity: 0.28,
          }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(20,18,16,0.55) 0%, rgba(20,18,16,0.75) 60%, #141210 100%)",
          }}
        />
        <div className="glow-green -top-20 -left-40" />
        <div className="glow-blue top-40 -right-40" />

        <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 w-full">
          <div className="max-w-5xl">
            <h1
              className="font-heading font-black text-5xl sm:text-6xl md:text-7xl lg:text-8xl uppercase leading-[0.9] tracking-tight"
              data-testid="hero-title"
            >
              Welcome to<br />
              <span className="text-deepblue-glow">RVA</span>{" "}
              <span className="text-forest-glow">Students</span>
            </h1>

            <p
              className="mt-8 font-heading font-bold text-xl md:text-2xl uppercase tracking-tight max-w-xl leading-tight text-white/80"
              data-testid="hero-tagline"
            >
              One City. <span className="text-forest-glow">One Generation.</span>{" "}
              <span className="text-deepblue-glow">One Mission.</span>
            </p>

            <div className="mt-10 flex flex-col sm:flex-row items-start gap-3">
              <Link to="/gatherings#rsvp" className="btn-primary" data-testid="hero-cta-button">
                RSVP — I'm Coming <ArrowUpRight size={16} />
              </Link>
              <ShareButton label="Invite A Friend" testId="hero-share-button" />
            </div>
          </div>
        </div>

        {/* Simple gathering card */}
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 w-full mt-16 md:mt-24">
          <div className="card-glass p-8 md:p-10" data-testid="hero-next-gathering-card">
            <div className="grid md:grid-cols-4 gap-6 md:items-center">
              <div className="md:col-span-1">
                <p className="overline mb-2">Next Gathering</p>
                <p className="font-heading font-black text-2xl uppercase">
                  Prayer + Worship Night
                </p>
              </div>
              <div className="flex items-center gap-3">
                <Calendar className="text-forest-glow" size={20} />
                <div>
                  <p className="text-xs text-white/50 uppercase tracking-widest">Date</p>
                  <p className="font-heading font-bold text-base">Sat · Nov 7</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Clock className="text-deepblue-glow" size={20} />
                <div>
                  <p className="text-xs text-white/50 uppercase tracking-widest">Time</p>
                  <p className="font-heading font-bold text-base">6:30 PM</p>
                </div>
              </div>
              <div className="flex items-center gap-3 md:justify-end">
                <MapPin className="text-forest-glow" size={20} />
                <div>
                  <p className="text-xs text-white/50 uppercase tracking-widest">Location</p>
                  <p className="font-heading font-bold text-base">3324 W Cary St · C</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WELCOME + MISSION */}
      <section className="relative py-20 md:py-28 overflow-hidden" data-testid="home-welcome-section">
        <div className="glow-green top-10 -right-40" />
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <h2 className="font-heading font-black text-4xl md:text-5xl lg:text-6xl uppercase leading-[0.95] tracking-tight">
              You're already<br /><span className="text-forest-glow">invited.</span>
            </h2>
          </div>
          <div className="lg:col-span-7 lg:pt-2">
            <p className="text-xl md:text-2xl leading-relaxed">
              RVA Students is a monthly gathering for high school students across Richmond.
              Whether you go to church every week, haven't been in a while, or are just
              curious about faith — <span className="font-semibold">you are welcome here.</span>
            </p>
            <div className="hr-line my-8" />
            <p className="font-heading font-bold text-xl md:text-2xl leading-snug tracking-tight">
              To unite high school students across Richmond around{" "}
              <span className="text-forest-glow">Jesus</span>, building a generation marked by
              faith, authentic <span className="text-deepblue-glow">community</span>, and a heart to
              make Him known.
            </p>
          </div>
        </div>
      </section>

      {/* WHAT WE DO */}
      <section className="relative py-20 md:py-28 overflow-hidden" data-testid="home-what-we-do">
        <div className="glow-blue top-40 -left-40" />
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-12">
          <h2 className="font-heading font-black text-4xl md:text-5xl lg:text-6xl uppercase leading-[0.95] tracking-tight max-w-2xl mb-12 md:mb-16">
            Four rhythms.<br />
            <span className="text-forest-glow">One</span> gathering.
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {pillars.map((p, i) => {
              const Icon = p.icon;
              return (
                <div
                  key={p.title}
                  className="card-glass p-8"
                  data-testid={`pillar-card-${p.title.toLowerCase()}`}
                >
                  <div className="w-10 h-10 rounded-xl bg-forest-glow/5 flex items-center justify-center mb-6">
                    <Icon size={18} className={i % 2 === 0 ? "text-forest-glow" : "text-deepblue-glow"} />
                  </div>
                  <h3 className="font-heading font-black text-2xl uppercase tracking-tight mb-3">
                    {p.title}
                  </h3>
                  <p className="text-white/60 leading-relaxed">{p.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA STRIP */}
      <section className="relative py-20 md:py-28 overflow-hidden" data-testid="home-cta-strip">
        <div className="glow-green top-0 left-1/4" />
        <div className="glow-blue bottom-0 right-1/4" />
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 text-center">
          <h2 className="font-heading font-black text-4xl md:text-5xl lg:text-6xl uppercase leading-[0.95] tracking-tight max-w-4xl mx-auto">
            You don't need to have it{" "}
            <span className="text-forest-glow italic font-normal">all figured out.</span>{" "}
            Just come.
          </h2>
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link to="/gatherings" className="btn-primary" data-testid="home-cta-gatherings">
              See Next Gathering <ArrowUpRight size={16} />
            </Link>
            <Link to="/get-involved" className="btn-ghost" data-testid="home-cta-involved">
              Get Involved
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default HomePage;
